import { useMsal } from '@azure/msal-react';
import { Link, Spinner, Text } from '@fluentui/react-components';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { downloadFile } from '../../../app-stores';
import { useCommonStyles, useStores } from '../../../common/hooks';
import { ResourceTicketRequest, ResourceTypes } from '../../../models/resource-services';
import { isPdfFileName } from '../../../shared/functions';
import { CommonLabelAndValueList } from '../../shared-components';
import { FlexBox } from '../../shared-components/flex-box';

const NominationSubmissionSectionImpl: React.FC = () => {
  const {
    instance,
  } = useMsal();

  const {
    apiStore,
    apiEndpointCallStatesStore,
    reviewAndSubmitNominationPageUiStore,
  } = useStores();

  const isDownloadServiceInFlight = apiEndpointCallStatesStore.inFlightStatusMap.get('postResourceTicket') ?? false;

  const commonStyles = useCommonStyles();

  const download = async (req: ResourceTicketRequest): Promise<void> => {
    try {
      const resourceTicket = await apiStore.postResourceTicket(instance, req);
      downloadFile(resourceTicket.ticketId);
    } catch (error) {
      // TODO - handle error
    }
  };

  const handleDownloadExportedNomination = async (): Promise<void> => {
    if (!reviewAndSubmitNominationPageUiStore.currentNomination) return;

    await download({
      resourceType: ResourceTypes.ExportedNomination,
      data: {
        routeData: {
          nominationId: reviewAndSubmitNominationPageUiStore.currentNomination.nominationId,
        },
        queryString: {
          exportType: 'PDF',
        },
      },
    });
  };

  const handleDownloadSupportingDocument = async (supportingDocumentId: number, isDisplayedAsInline: boolean): Promise<void> => {
    await download({
      data: {
        routeData: {
          supportingDocumentId: supportingDocumentId,
        },
        queryString: {
          isDisplayedAsInline: `${isDisplayedAsInline}`,
        },
      },
      resourceType: ResourceTypes.SupportingDocument,
    });
  };

  return (
    <FlexBox
      wrap
      isFullWidth
      as='section'
      className='nomination-section'
    >
      <Text
        as='h3'
        block
        weight='semibold'
        size={400}
      >
        Nomination submission
      </Text>
      <FlexBox
        isFullWidth
        direction='column'
        className='nomination-section__list'
      >
        <FlexBox
          isFullWidth
          aligns='start'
        >
          <CommonLabelAndValueList
            label='Submission:'
          >
            <Link
              as='button'
              className={commonStyles.buttonAsLink}
              onClick={handleDownloadExportedNomination}
            >
              {
                isDownloadServiceInFlight && (
                  <Spinner
                    size='extra-tiny'
                  />
                )
              }
              Preview as PDF
            </Link>
          </CommonLabelAndValueList>
          <CommonLabelAndValueList
            label='Websites:'
          >
            <FlexBox
              as='ul'
              direction='column'
              aligns='start'
            >
              {
                reviewAndSubmitNominationPageUiStore.currentNomination?.websites.map((website) => (
                  <li
                    key={website.websiteId}
                  >
                    <Link
                      href={website.websiteURL}
                      rel='noopener noreferrer'
                      target='_blank'
                    >
                      {website.websiteURL}
                    </Link>
                  </li>
                ))
              }
            </FlexBox>
          </CommonLabelAndValueList>
        </FlexBox>
        <FlexBox
          isFullWidth
          aligns='start'
        >
          <CommonLabelAndValueList
            label='Supporting document:'
          >
            <FlexBox
              as='ul'
              direction='column'
              aligns='start'
            >
              {
                reviewAndSubmitNominationPageUiStore.currentNomination?.supportingDocuments.map((supportingDocument) => (
                  <li
                    className='supporting-document'
                    key={supportingDocument.documentId}
                  >
                    <Link
                      as='button'
                      className={commonStyles.buttonAsLink}
                      onClick={() => handleDownloadSupportingDocument(supportingDocument.documentId, isPdfFileName(supportingDocument.fileName))}
                    >
                      {supportingDocument.fileName}
                    </Link>
                  </li>
                ))
              }
            </FlexBox>
          </CommonLabelAndValueList>
        </FlexBox>
      </FlexBox>
    </FlexBox>
  );
}

export const NominationSubmissionSection = observer(NominationSubmissionSectionImpl);
